// import hooks
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useForceRefresh from 'components/shared/hooks/useForceRefresh';

//import api's and mutations
import { CLA_YEAR_SETTINGS } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

// import components
import { yupResolver } from '@hookform/resolvers/yup';
import { showSnackbar } from 'components/shared';
import EditPage from 'components/shared/CRUD/components/InnerPage/EditPage';
import TabsAtClaSettings from './DetailView';

// import utils
import { generalSchema } from './CLASettings.schema';
import {
  convertFlagsArrayToObjects,
  convertFlagsObjectToArray
} from '../../../../utils/misc';
import { omit } from 'lodash';

// import types
import {
  CLA_SETTINGS_FLAG,
  CLAItemFormValues,
  EditCLAProps,
  type CLAItem
} from './CLASettings.types';

const EditCLA: React.FC<EditCLAProps> = ({
  toggleInnerLayout,
  rows,
  rowData
}) => {
  const { t } = useTranslation('common');

  const [currentViewData, setCurrentViewData] = useState<CLAItemFormValues>();

  const [currentSchema, setCurrentSchema] = useState(generalSchema(t));
  const { timestamp: isEditable, refresh: setIsEditable } = useForceRefresh();

  const { mutateAsync: save } = useSaveResource({
    id: currentViewData?.claYearId ?? rowData.claYearId,
    url: CLA_YEAR_SETTINGS,
    invalidateQueryKey: 'claYears'
  });

  const toFormValue = (data: CLAItemFormValues | CLAItem | undefined) => {
    if (!data) {
      return undefined;
    }

    const plbValidity = () => {
      if (Array.isArray(data.flags)) {
        return data.flags.includes(CLA_SETTINGS_FLAG.PLB_5_YEARS)
          ? CLA_SETTINGS_FLAG.PLB_5_YEARS
          : data.flags.includes(CLA_SETTINGS_FLAG.PLB_10_YEARS)
          ? CLA_SETTINGS_FLAG.PLB_10_YEARS
          : '';
      } else {
        return (data.flags as any).PLB_5_YEARS
          ? CLA_SETTINGS_FLAG.PLB_5_YEARS
          : (data.flags as any).PLB_10_YEARS
          ? CLA_SETTINGS_FLAG.PLB_10_YEARS
          : '';
      }
    };

    const newData: CLAItemFormValues = {
      ...data,
      rowId: data.claYearId,
      flags: Array.isArray(data.flags)
        ? convertFlagsArrayToObjects(data.flags)
        : data.flags,
      workHoursBasedWeekdays: !!data.workHoursBasedWeekdays,
      plbValidity: plbValidity()
    };

    return newData;
  };

  useEffect(() => {
    setCurrentViewData(toFormValue(rowData));
  }, [rowData]);

  const methods = useForm({
    defaultValues: currentSchema.cast(currentViewData),
    resolver: yupResolver(currentSchema),
    mode: 'onChange'
  });

  useEffect(() => {
    methods.reset({
      ...currentSchema.cast(toFormValue(currentViewData))
    });
  }, [currentViewData]);

  const onSubmit = async (data) => {
    const updatedFlags = {
      ...data.flags,
      [CLA_SETTINGS_FLAG.PLB_5_YEARS]:
        data.plbValidity === CLA_SETTINGS_FLAG.PLB_5_YEARS,
      [CLA_SETTINGS_FLAG.PLB_10_YEARS]:
        data.plbValidity === CLA_SETTINGS_FLAG.PLB_10_YEARS
    };

    const payload = {
      ...omit(data, [
        'plbValidity',
        'flags',
        'specialLeavePercentage',
        'specialLeavePeriod',
        'specialLeaveProcessAsNegative',
        'specialLeaveImportCode',
        'specialLeaveDutyHourTypeId'
      ]),
      flags: convertFlagsObjectToArray(updatedFlags),
      workHoursBasedWeekdays: data.workHoursBasedWeekdays ? 1 : 0
    };

    await save(payload, {
      onSuccess: () => {
        showSnackbar({ message: t('changesSaved'), severity: 'success' });
        setIsEditable();
        methods.reset(data);
      },
      onError: () => {
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
      }
    });
  };

  return (
    <EditPage
      onCancel={() => methods.reset()}
      methods={methods}
      onSubmit={methods.handleSubmit(onSubmit)}
      toggleInnerLayout={toggleInnerLayout}
      currentView={currentViewData}
      setCurrentView={setCurrentViewData}
      views={rows}
      isEditable={isEditable}
      navigatorLabelGetter={['code', 'year']}
      navigatorValueGetter="claYearId"
      withTabs={true}
    >
      <TabsAtClaSettings setCurrentSchema={setCurrentSchema} />
    </EditPage>
  );
};

export default EditCLA;
